import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type ExpenseResource,
  ExpensesController_record_v1,
  type ExpensesController_record_v1Body,
  type OutletResource,
  OutletsController_list_v1,
  type VendorResource,
  VendorsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCircleCheck,
  IconHistory,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { isoInstantFrom, localInstantOf } from '@/lib/local-instant';
import { formatPaise, paiseFromRupees } from '@/lib/money';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { Screen, ScreenFact } from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { paths } from '../paths';
import { labelForPaymentMode } from '../sales/payment-modes';
import { formatBusinessDate } from '../tickets/business-date';
import {
  EXPENSE_PAYMENT_MODES,
  expenseSchema,
  type ExpenseValues,
  fingerprintOf,
  isExpensePaymentMode,
  outcomeOfStatus,
  type RecordOutcome,
} from './expense-form';

/**
 * Money that left an outlet for something:
 * `POST /v1/outlets/{outletId}/expenses`.
 *
 * **The only write in the Reporting epic**, and so the only screen in it with a
 * form. Everything else in the section aggregates over data that already
 * existed.
 *
 * ## Two successes, and they are not the same event
 *
 * `201` recorded it. `200` means the server **already had this expense** — the
 * same `id` with the same facts — and wrote nothing further. The document is
 * emphatic that the two bodies are identical, "which is what leaves the status
 * as the only place the difference lives", so this screen reads the status
 * through `onStatus` and says which of the two happened. A screen that flattened
 * them would tell somebody who double-submitted, or whose connection retried,
 * that it recorded a second expense — while the server did exactly the right
 * thing and there is no second row to find.
 *
 * The terminal's outbox already draws this distinction, in the same words, in
 * `apps/desktop/src/main/sync/errors.ts`. The two are not one module: that is an
 * Electron queue and this is a browser form, and they share no code today.
 *
 * ## One key while the body stands, a fresh one when it moves
 *
 * This route is `@Idempotent` and the header is required — without it the
 * request is refused before anything is written. The payload here is editable,
 * so neither of the two simpler patterns fits, and `lib/write-attempt.ts` states
 * the third at length: a key held for the component's life earns `409
 * SYNC_IDEMPOTENCY_KEY_REUSED` the first time somebody fixes a typo and saves
 * again, while a key minted per press writes the expense twice if an answer is
 * ever lost. The key and the body's `id` are minted together, per body, and held
 * for as long as that body stands.
 *
 * **The attempt is deliberately kept after a `200`.** The write did not happen
 * twice and pressing again would replay again; only a `201` clears it, because
 * only then is the next send genuinely a different act.
 *
 * ## The outlet is in the path, and the date is nobody's
 *
 * `RecordExpenseDto` does not define `outletId` at all — a body carrying it
 * earns `400 VALIDATION_UNKNOWN_FIELD` — so the picker's value goes into the
 * path (FR-705a). And there is no business-date field on this form because the
 * route takes none: an expense attaches to the outlet's **current** open trading
 * day, resolved by the server, precisely so nobody can move spending between
 * days after the fact. `occurredAt` is beside it and is a different statement —
 * when the money went out, as the person recording it says.
 *
 * ## Refusal copy is not written here
 *
 * `EXPENSE_AMOUNT_INVALID` and `EXPENSE_REASON_INVALID` are branched on by
 * `code` and put on the field they concern; the sentence comes from
 * `messageForFailure`, which is the only thing that words a refusal in this app.
 * Both are 422 and neither is retryable.
 */

/** The vendor picker's "none". Not `''`, which Base UI clears to. */
const NO_VENDOR = 'no-vendor';

/** What the server answered, and which of the two successes it was. */
interface Recorded {
  readonly expense: ExpenseResource;
  readonly outcome: RecordOutcome;
}

export function RecordExpense() {
  const client = useApiClient();

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  // Whether the outlets have answered *at all*, which is not the same question
  // as whether there are any. Without it the notice under an empty picker reads
  // as a statement about the park for as long as the first request takes.
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  // Active vendors only: the API refuses a retired one. A failed read — or an
  // API that does not serve vendors yet — leaves the picker on "No vendor".
  const [vendors, setVendors] = useState<readonly VendorResource[]>([]);

  const [formError, setFormError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<Recorded | null>(null);

  /**
   * The key and the id this form is currently attempting under.
   *
   * A ref rather than state: nothing renders from it, and it must be read and
   * written inside one submit without waiting for a render to see the new value.
   */
  const attempt = useRef<WriteAttempt | null>(null);

  const form = useForm<ExpenseValues>({
    resolver: zodResolver(expenseSchema),
    mode: 'onTouched',
    defaultValues: {
      outletId: '',
      amount: '',
      reason: '',
      // Cash first, because it is the mode that has to reconcile against a
      // physical drawer and is the common case for a park expense. It is a
      // prefill and not a default the API holds — `paymentMode` is required
      // there and has no default of its own.
      paymentMode: 'CASH',
      // Prefilled with now, from the injected clock rather than from
      // `new Date()` — the common case is recording what has just been paid, and
      // the field stays editable because a Tuesday payment entered on Thursday
      // has to be able to say so.
      at: localInstantOf(systemClock),
      vendorId: NO_VENDOR,
      receiptNumber: '',
    },
  });

  const outletId = useWatch({ control: form.control, name: 'outletId' });
  const amount = useWatch({ control: form.control, name: 'amount' });

  const pending = form.formState.isSubmitting;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired ones are
    // excluded by the API's own default, and correctly — an expense at a retired
    // outlet is refused `404`, indistinguishably from one that never existed.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);
      })
      .catch(() => {
        // The picker is the only casualty and the screen says so where the
        // picker is, rather than replacing a form somebody may be halfway
        // through with an error about a control.
        if (live) setOutlets([]);
      });

    collectPages<VendorResource>((offset, limit) =>
      client.request(VendorsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setVendors(all);
      })
      .catch(() => {
        if (live) setVendors([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [outlets],
  );

  const vendorItems = useMemo(
    () => [
      { label: 'No vendor', value: NO_VENDOR },
      ...vendors.map((vendor) => ({ label: vendor.name, value: vendor.id })),
    ],
    [vendors],
  );

  const modeItems = useMemo(
    () => EXPENSE_PAYMENT_MODES.map((value) => ({ label: labelForPaymentMode(value), value })),
    [],
  );

  const outlet = outlets.find((candidate) => candidate.id === outletId);

  /**
   * A refusal, put where the thing it is about is.
   *
   * The copy is never written here. `messageForFailure` turns the code into a
   * sentence and is the only thing that does; what this decides is *which field*
   * the sentence belongs to, which is the half the screen can answer and the API
   * cannot.
   */
  const recordFailure = (error: unknown) => {
    if (error instanceof ApiError && error.code === 'EXPENSE_AMOUNT_INVALID') {
      // Zero, negative, fractional, or past the bound. The schema refuses all
      // four before the wire, so this is a body that got past it — a bound moved
      // upstream, or a tab left open across a deploy.
      form.setError('amount', { message: messageForFailure(error) });

      return;
    }

    if (error instanceof ApiError && error.code === 'EXPENSE_REASON_INVALID') {
      // Absent, blank once trimmed, or past the bound. Same three the schema
      // holds, and the same argument for keeping the mapping wired anyway.
      form.setError('reason', { message: messageForFailure(error) });

      return;
    }

    if (error instanceof ApiError && error.code === 'EXPENSE_VENDOR_UNAVAILABLE') {
      // Retired since this screen loaded its vendors.
      form.setError('vendorId', { message: messageForFailure(error) });

      return;
    }

    if (error instanceof ApiError && error.code === 'RESOURCE_NOT_FOUND') {
      // No such outlet, one retired since this screen loaded, or one outside
      // this account's reach — the three are indistinguishable by design
      // (FR-724, FR-128), so it is said about the picker rather than guessed at.
      form.setError('outletId', { message: messageForFailure(error) });

      return;
    }

    setFormError(messageForFailure(error));
  };

  const handleSubmit = async (values: ExpenseValues) => {
    // Belt and braces with the disabled button below. This route *adds* to an
    // append-only table and an expense cannot be edited or deleted, so a second
    // submit that got past both is a second row that only a correction can
    // answer.
    if (pending) return;

    // The resolver has already refused everything these two can fail for. Read
    // as impossible states rather than asserted away with a `!`: a change to the
    // schema that let a bad value through shows as a field that refuses rather
    // than as a body carrying `undefined`.
    const occurredAt = isoInstantFrom(values.at);

    if (occurredAt === null) {
      form.setError('at', { message: 'That is not a real date and time.' });

      return;
    }

    if (!isExpensePaymentMode(values.paymentMode)) {
      form.setError('paymentMode', { message: 'Choose how the expense was paid.' });

      return;
    }

    setFormError(null);
    setRecorded(null);

    // Everything but the id, which is minted with the key below because the two
    // together are what makes one attempt one write.
    const content = {
      // Integer paise, moved as digits by `lib/money.ts` and never by
      // multiplying a decimal. The schema has already refused anything
      // `RUPEES_SHAPE` does not match and anything outside the published bound.
      amountPaise: paiseFromRupees(values.amount),

      // Sent exactly as typed. The API stores a reason "exactly as submitted"
      // and the record cannot afterwards be edited, so a client that quietly
      // trimmed it would be editing something nobody can put right. The schema
      // has already refused one that is blank once trimmed.
      reason: values.reason,

      paymentMode: values.paymentMode,
      occurredAt,

      // Omitted rather than `null` for "none": the same meaning to the API, and
      // a body an API older than #512 still accepts.
      ...(values.vendorId === NO_VENDOR ? {} : { vendorId: values.vendorId }),
      ...(values.receiptNumber === '' ? {} : { receiptNumber: values.receiptNumber }),
    };

    const sending = attemptFor(attempt.current, fingerprintOf(values.outletId, content));

    attempt.current = sending;

    try {
      // The body's shape comes from the generated client. Nothing about this
      // route's path or payload is written down in this file.
      const body: ExpensesController_record_v1Body = { id: sending.id, ...content };

      // `201` or `200`, and the bodies are identical. Captured here because the
      // status is the only place the difference lives — see the note at the head
      // of this file, and `RequestExtras.onStatus`.
      let outcome: RecordOutcome = 'recorded';

      const expense = await client.request(ExpensesController_record_v1, {
        path: { outletId: values.outletId },
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
        onStatus: (status) => {
          outcome = outcomeOfStatus(status);
        },
      });

      setRecorded({ expense, outcome });

      if (outcome === 'recorded') {
        // Written. The next expense is a different act however similar it looks
        // — two identical purchases on one day are two real expenses and must
        // not replay the first one's answer.
        attempt.current = null;

        // The outlet and the payment mode are kept, because somebody recording
        // one expense is often recording two at the same place. The moment is
        // refreshed rather than left at the old one, which would quietly
        // backdate the next record.
        form.reset({
          outletId: values.outletId,
          amount: '',
          reason: '',
          paymentMode: values.paymentMode,
          at: localInstantOf(systemClock),
          vendorId: NO_VENDOR,
          receiptNumber: '',
        });
      }

      // On a `200` nothing is reset and the attempt is deliberately kept: the
      // form still holds the expense the server already has, and pressing again
      // replays it again rather than writing a second one. Clearing the fields
      // here would invite somebody to retype what is already recorded.
    } catch (error: unknown) {
      // Nothing is cleared. The retry is usually one corrected field, and
      // re-typing a whole expense to fix it is a cruelty this form is in no
      // position to inflict. The attempt is left in place too — if the body is
      // unchanged, pressing again replays under the same key.
      recordFailure(error);
    }
  };

  // What the amount field currently says, in the shape it will be recorded as.
  // Shown because the field takes rupees and the record is paise, and a person
  // typing `450.5` should see `₹450.50` before they commit to it.
  const previewPaise = /^\d+(\.\d{1,2})?$/.test(amount.trim())
    ? paiseFromRupees(amount.trim())
    : null;

  return (
    <Screen
      title="Record an expense"
      // Only when there is something standing to say. Both remaining facts are
      // conditional now that the trading day has moved to the margin, and
      // `Screen` draws the band for a fragment that renders nothing.
      aside={
        outlet === undefined && previewPaise === null ? undefined : (
          <>
            {outlet !== undefined && (
              <ScreenFact label="Outlet">
                <span className="font-mono numeric">{outlet.code}</span> — {outlet.name}
                {isRetired(outlet) && (
                  <>
                    {' '}
                    <Badge variant="outline">Retired</Badge>
                  </>
                )}
              </ScreenFact>
            )}

            {previewPaise !== null && (
              <ScreenFact label="Amount">
                <span className="font-mono numeric">{formatPaise(previewPaise)}</span>
              </ScreenFact>
            )}
          </>
        )
      }
      // The related screen beside the fields, and the whole of what says
      // "this screen is a form".
      guidance={
        <>
          {/* The related screen, in the margin. */}
          <div className="space-y-2 text-sm">
            <Link
              to={paths.expenses}
              className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              Every expense, newest first
              <IconArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </>
      }
    >
      {recorded !== null && <RecordedNotice recorded={recorded} />}

      <Form {...form}>
        {/* One column, per docs/ui-conventions.md. `noValidate` because the
              browser's own bubbles are not this app's error language. */}
        <form
          className="space-y-6"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="outletId"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Outlet
                </FormLabel>
                {/* Not wrapped in `FormControl`: the trigger is a button
                      rather than the input the generated id belongs on. */}
                <Select
                  value={field.value}
                  items={outletItems}
                  onValueChange={(next) => {
                    // Base UI types the value as nullable because a select may
                    // be cleared. `''` is what this form calls "nothing
                    // chosen", and the schema refuses it.
                    field.onChange(next ?? '');
                    setRecorded(null);
                    setFormError(null);
                  }}
                  disabled={outlets.length === 0}
                >
                  <SelectTrigger
                    id="expense-outlet"
                    aria-label="Outlet"
                    aria-invalid={form.formState.errors.outletId !== undefined}
                    aria-describedby={
                      form.formState.errors.outletId === undefined
                        ? undefined
                        : 'expense-outlet-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue placeholder="Choose an outlet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {outlets.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {outletsAnswered && outlets.length === 0 && (
                  <FormDescription>This account is not posted to any outlet.</FormDescription>
                )}
                <FormMessage id="expense-outlet-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Amount in rupees
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      // `inputMode` and not `type="number"`, which hands the
                      // browser its own opinion — a spinner, a wheel that
                      // changes the figure under a pointer, and a value that
                      // reads back empty as `NaN`. An amount is read aloud and
                      // typed in, so it gets the mono face and tabular
                      // figures.
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder="450.00"
                      className={`${fieldSurface} h-11 w-full font-mono numeric`}
                      required
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  What it was for
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      // An `Input` and not a `textarea`: the registry has not
                      // published one into `packages/ui`, and the two reason
                      // fields already in this app — a void and a refund — are
                      // both single-line for the same reason. Adding one is a
                      // `shadcn add`, which lists `button` among its registry
                      // dependencies and has silently eaten the button's
                      // `touch` sizes three times. Noted in the PR body as the
                      // control this field actually wants.
                      className={`${fieldSurface} h-11 w-full`}
                      autoComplete="off"
                      placeholder="Vegetables for the cafe"
                      required
                      {...field}
                    />
                  }
                />
                <FormDescription>Cannot be edited afterwards.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vendorId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>Paid to</FormLabel>
                <Select
                  value={field.value}
                  items={vendorItems}
                  onValueChange={(next) => {
                    field.onChange(next ?? NO_VENDOR);
                  }}
                >
                  <SelectTrigger
                    id="expense-vendor"
                    aria-label="Paid to"
                    aria-invalid={form.formState.errors.vendorId !== undefined}
                    aria-describedby={
                      form.formState.errors.vendorId === undefined
                        ? undefined
                        : 'expense-vendor-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {vendorItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormDescription>Optional.</FormDescription>
                <FormMessage id="expense-vendor-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="receiptNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={fieldLabel}>Receipt number</FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-11 w-full font-mono numeric`}
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="INV-2291"
                      {...field}
                    />
                  }
                />
                <FormDescription>Optional.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="paymentMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  How it was paid
                </FormLabel>
                <Select
                  value={field.value}
                  items={modeItems}
                  onValueChange={(next) => {
                    field.onChange(next ?? '');
                  }}
                >
                  <SelectTrigger
                    id="expense-mode"
                    aria-label="How it was paid"
                    aria-invalid={form.formState.errors.paymentMode !== undefined}
                    aria-describedby={
                      form.formState.errors.paymentMode === undefined
                        ? undefined
                        : 'expense-mode-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue placeholder="Choose how it was paid" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {EXPENSE_PAYMENT_MODES.map((option) => (
                        <SelectItem key={option} value={option}>
                          {labelForPaymentMode(option)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage id="expense-mode-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="at"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  When the money went out
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      // A native date-and-time field rather than a box asking
                      // for ISO 8601, and typed in the zone every instant in
                      // this app is rendered in.
                      type="datetime-local"
                      className={`${fieldSurface} h-11 w-full numeric`}
                      required
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Above the submit and it stays there — a toast vanishes, and
                somebody who looked away has no way to get it back. */}
          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            // The one primary action on the screen, and the only control that
            // writes anything. Disabled while in flight, which is what stops a
            // second press becoming a second expense.
            disabled={pending || outlets.length === 0}
          >
            {pending ? 'Recording…' : 'Record expense'}
          </Button>
        </form>
      </Form>
    </Screen>
  );
}

/**
 * What the server answered — and **which of its two successes** it was.
 *
 * `201` and `200` carry identical bodies, so this is the one place in the screen
 * where they are told apart, and it is told apart from the status alone. The
 * difference is stated rather than smoothed over: somebody who pressed twice, or
 * whose connection retried, is told that the expense they are looking at is the
 * one already recorded and that nothing was written a second time. Reassurance,
 * not a warning — `role="status"` and no destructive colour, because nothing
 * went wrong.
 */
function RecordedNotice({ recorded }: { recorded: Recorded }) {
  const { expense, outcome } = recorded;
  const replayed = outcome === 'already-recorded';

  return (
    // `status` and not the component's default `alert`: an expense that was
    // recorded is not an interruption.
    <Alert role="status">
      {replayed ? <IconHistory /> : <IconCircleCheck />}
      <AlertTitle>{replayed ? 'Already recorded' : 'Recorded'}</AlertTitle>
      <AlertDescription>
        <span>
          <span className="font-mono numeric">{formatPaise(expense.amountPaise)}</span>{' '}
          {labelForPaymentMode(expense.paymentMode).toLowerCase()}, against the trading day{' '}
          <span className="numeric">{formatBusinessDate(expense.businessDate)}</span>.
          {replayed && ' Nothing was written a second time.'}
        </span>
      </AlertDescription>
    </Alert>
  );
}
